import { ClientPrincipal, UserInfo } from '../types';
import { apiService } from './api';

class AuthService {
  // Azure Static Web Apps provides user info at this endpoint
  private readonly AUTH_ME_ENDPOINT = '/.auth/me';

  async getUserInfo(): Promise<UserInfo> {
    try {
      const response = await fetch(this.AUTH_ME_ENDPOINT);
      const authData = await response.json();
      
      // Azure Static Web Apps returns an array with user info
      const clientPrincipal: ClientPrincipal | null = authData.clientPrincipal || null;
      
      return {
        clientPrincipal,
        employee: undefined
      };
    } catch (error) {
      console.error('Failed to get user info:', error);
      return {
        clientPrincipal: null,
        employee: undefined
      };
    }
  }

  async getUserInfoWithEmployee(): Promise<UserInfo> {
    try {
      // Use the new authenticated endpoint to get current user with employee data
      const currentEmployeeResponse = await apiService.getCurrentEmployee();
      
      if (currentEmployeeResponse.success && currentEmployeeResponse.data) {
        // API returned both authentication info and employee data
        return currentEmployeeResponse.data;
      }
      
      // If the API call failed, fall back to basic auth info
      const userInfo = await this.getUserInfo();
      
      if (currentEmployeeResponse.message?.includes('Authentication required')) {
        // User is not authenticated
        return userInfo;
      }
      
      if (currentEmployeeResponse.message?.includes('Employee record not found')) {
        // User is authenticated but no employee record exists
        console.warn('Authenticated user has no employee record');
        return userInfo;
      }
      
      // Other API error - log it but return basic auth info
      console.error('Failed to get current employee:', currentEmployeeResponse.message);
      return userInfo;
      
    } catch (error) {
      console.error('Failed to get user info with employee:', error);
      // Fall back to basic user info if API call fails completely
      return this.getUserInfo();
    }
  }

  isAuthenticated(userInfo: UserInfo): boolean {
    return userInfo.clientPrincipal !== null;
  }

  getUserEmail(userInfo: UserInfo): string | null {
    return userInfo.clientPrincipal?.userDetails || null;
  }

  getUserId(userInfo: UserInfo): string | null {
    return userInfo.clientPrincipal?.userId || null;
  }

  getIdentityProvider(userInfo: UserInfo): string | null {
    return userInfo.clientPrincipal?.identityProvider || null;
  }

  // Generate login URLs for different providers
  getLoginUrl(provider: 'github' | 'google' | 'facebook' | 'twitter' = 'github', redirectUrl?: string): string {
    const redirect = redirectUrl ? `?post_login_redirect_uri=${encodeURIComponent(redirectUrl)}` : '';
    return `/.auth/login/${provider}${redirect}`;
  }

  // Generate logout URL
  getLogoutUrl(redirectUrl?: string): string {
    const redirect = redirectUrl ? `?post_logout_redirect_uri=${encodeURIComponent(redirectUrl)}` : '';
    return `/.auth/logout${redirect}`;
  }

  // Redirect to login
  login(provider: 'github' | 'google' | 'facebook' | 'twitter' = 'github'): void {
    window.location.href = this.getLoginUrl(provider, window.location.origin);
  }

  // Redirect to logout
  logout(): void {
    window.location.href = this.getLogoutUrl(window.location.origin);
  }
}

export const authService = new AuthService();
